import axios from "axios";
import apiEndpointManager from "../config"; // Assuming default export
import {
    UserSuggestion,
    PostSuggestion,
    UserSearchResult,
    PostSearchResult,
} from "../store/slices/searchSlice"; // Import types from searchSlice

// Type for the raw response from the suggestions API
// The backend directly returns an array of UserSuggestion or PostSuggestion
export type SuggestionsAPIResponse = (UserSuggestion | PostSuggestion)[];

// Type for the raw response from the full search API (matches backend structure)
export interface FullSearchResultsAPIResponse {
    results: UserSearchResult[] | PostSearchResult[];
    lastEvaluatedKey: string | null;
}

const getApiRoot = async () => {
    return await apiEndpointManager.getEndpoint();
};

// Service functions
export const searchService = {
    getSearchSuggestions: async (
        query: string,
        type: "posts" | "users",
    ): Promise<UserSuggestion[] | PostSuggestion[]> => {
        const API_ROOT = await getApiRoot();
        const requestUrl = `${API_ROOT}/search/suggestions`;
        const requestParams = { q: query, type };
        console.log(
            `[SearchService] Fetching suggestions from: ${requestUrl} with params:`,
            requestParams,
        );

        try {
            const response = await axios.get<UserSuggestion[] | PostSuggestion[]>(requestUrl, {
                params: requestParams,
            });
            console.log(
                `[SearchService] Suggestions response for query "${query}", type "${type}":`,
                response.data,
            );
            return response.data;
        } catch (error) {
            console.error(
                `[SearchService] Error fetching search suggestions for query "${query}", type "${type}":`,
                error,
            );
            if (axios.isAxiosError(error) && error.response) {
                console.error(
                    "[SearchService] Suggestions error response data:",
                    error.response.data,
                );
                console.error(
                    "[SearchService] Suggestions error response status:",
                    error.response.status,
                );
                console.error(
                    "[SearchService] Suggestions error response headers:",
                    error.response.headers,
                );
                throw new Error(
                    error.response.data?.message || "Failed to fetch search suggestions",
                );
            } else if (error instanceof Error) {
                console.error(
                    "[SearchService] Suggestions non-Axios error message:",
                    error.message,
                );
                throw new Error(error.message);
            }
            console.error("[SearchService] Suggestions unknown error object:", error);
            throw new Error("An unknown error occurred while fetching search suggestions");
        }
    },

    getFullSearchResults: async (
        query: string,
        type: "posts" | "users",
        exclusiveStartKey?: string | null,
    ): Promise<FullSearchResultsAPIResponse> => {
        const API_ROOT = await getApiRoot();
        const requestUrl = `${API_ROOT}/search`;
        const requestParams: any = { q: query, type };
        if (exclusiveStartKey) {
            requestParams.exclusiveStartKey = exclusiveStartKey;
        }
        console.log(
            `[SearchService] Fetching full search results from: ${requestUrl} with params:`,
            requestParams,
        );

        try {
            const response = await axios.get<FullSearchResultsAPIResponse>(requestUrl, {
                params: requestParams,
            });
            console.log(
                `[SearchService] Full search results response for query "${query}", type "${type}", key "${exclusiveStartKey || ""}":`,
                response.data,
            );
            return response.data;
        } catch (error) {
            console.error(
                `[SearchService] Error fetching full search results for query "${query}", type "${type}", key "${exclusiveStartKey || ""}":`,
                error,
            );
            if (axios.isAxiosError(error) && error.response) {
                console.error(
                    "[SearchService] Full search error response data:",
                    error.response.data,
                );
                console.error(
                    "[SearchService] Full search error response status:",
                    error.response.status,
                );
                console.error(
                    "[SearchService] Full search error response headers:",
                    error.response.headers,
                );
                throw new Error(
                    error.response.data?.message || "Failed to fetch full search results",
                );
            } else if (error instanceof Error) {
                console.error(
                    "[SearchService] Full search non-Axios error message:",
                    error.message,
                );
                throw new Error(error.message);
            }
            console.error("[SearchService] Full search unknown error object:", error);
            throw new Error("An unknown error occurred while fetching full search results");
        }
    },
};

export default searchService;
